import { useState, useEffect, useCallback, useRef } from 'react';

type SetValue<T> = (value: T | ((prev: T) => T)) => void;

/**
 * Custom hook for managing localStorage state
 * @param key - The localStorage key
 * @param initialValue - The initial value
 * @returns [storedValue, setValue, removeValue]
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, SetValue<T>, () => void] {
  // Get from local storage then parse stored json or return initialValue
  const readValue = useCallback((): T => {
    // Prevent build error "window is undefined" but keep working
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Re-read synchronously the moment the key changes, before this render
  // commits — not in an effect. The key is often derived from async state
  // (e.g. the user-scoped `${userId}:…` prefix resolves a render after auth
  // hydrates). With an effect-based re-read the value lags one commit: it
  // reads `null` under the new key for that render, and any consumer effect
  // that auto-fills a default on `null` fires and persists garbage, clobbering
  // the real stored value before the re-read lands. Adjusting state during
  // render (React's documented pattern) makes the committed render already
  // hold the correct value, so no consumer ever observes the stale gap.
  const keyRef = useRef(key);
  if (keyRef.current !== key) {
    keyRef.current = key;
    setStoredValue(readValue());
  }

  // Ref tracks latest value so setValue can compute functional updates
  // without dispatching events inside a React state updater
  const storedValueRef = useRef(storedValue);
  storedValueRef.current = storedValue;

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue: SetValue<T> = useCallback(
    (value) => {
      // Prevent build error "window is undefined" but keeps working
      if (typeof window === 'undefined') {
        console.warn(
          `Tried setting localStorage key "${key}" even though environment is not a client`
        );
      }

      try {
        // Allow value to be a function so we have the same API as useState
        const newValue =
          value instanceof Function ? value(storedValueRef.current) : value;

        // Update ref immediately for rapid consecutive calls
        storedValueRef.current = newValue;

        // Save to local storage
        window.localStorage.setItem(key, JSON.stringify(newValue));

        // Update state
        setStoredValue(newValue);

        // Notify other useLocalStorage instances (must be outside state updater
        // to avoid triggering setState in other components during render)
        window.dispatchEvent(new Event('local-storage'));
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
      window.dispatchEvent(new Event('local-storage'));
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  useEffect(() => {
    const handleStorageChange = () => {
      setStoredValue(readValue());
    };

    // This only works for other documents, not the current one
    window.addEventListener('storage', handleStorageChange);

    // This is a custom event, triggered in setValue and removeValue
    window.addEventListener('local-storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage', handleStorageChange);
    };
  }, [readValue]);

  return [storedValue, setValue, removeValue];
}
